import { useRef, useState } from "react";
import { Loader2, Play } from "lucide-react";
import { cn } from "@/lib/utils";

interface CirclePlayerProps {
  src: string;
  className?: string;
}

/**
 * Round, controls-less video player. Click to toggle play/pause; native
 * timeline, fullscreen and other UI affordances are suppressed.
 *
 * Painting the poster frame fast:
 *   - `preload="metadata"` keeps the network footprint at the moov-box plus
 *     a small initial range (~tens of KB) instead of the whole file.
 *   - `#t=0.1` is a media fragment — the browser interprets it client-side
 *     (it never reaches Supabase Storage) and seeks 100 ms into the file,
 *     which forces it to decode and paint a real frame instead of leaving
 *     the element black.
 *   - On `loadedmetadata` we also explicitly bump `currentTime` to 0.1 as a
 *     belt-and-braces fix for browsers that ignore the URI fragment.
 *   - `setReady(true)` fires on the earliest of `loadedmetadata`,
 *     `loadeddata`, or `seeked` so Play becomes clickable the moment the
 *     poster frame is on screen.
 */
function withPosterFragment(url: string): string {
  // Don't double-append if the caller already added a media fragment.
  if (url.includes("#t=")) return url;
  return `${url}#t=0.1`;
}

export function CirclePlayer({ src, className }: CirclePlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v || !ready) return;
    if (v.paused) {
      void v.play();
    } else {
      v.pause();
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      onContextMenu={(e) => e.preventDefault()}
      className={cn(
        "group relative block overflow-hidden rounded-full bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb]",
        className,
      )}
      aria-label={playing ? "Pause pitch" : ready ? "Play pitch" : "Loading pitch"}
    >
      <video
        ref={videoRef}
        src={withPosterFragment(src)}
        playsInline
        preload="metadata"
        muted
        disablePictureInPicture
        controlsList="nodownload nofullscreen noremoteplayback"
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          // Force a tiny seek so iOS Safari (and some Chrome builds) decode
          // and paint a frame; without this the canvas stays black until
          // play() is called.
          try {
            if (v.currentTime < 0.05) v.currentTime = 0.1;
          } catch {
            /* Some streams reject seek on metadata-only state — ignore. */
          }
          setReady(true);
        }}
        onLoadedData={() => setReady(true)}
        onSeeked={() => setReady(true)}
        onPlay={(e) => {
          // Element starts muted so iOS Safari paints the poster frame
          // without an explicit user gesture; unmute on real play.
          e.currentTarget.muted = false;
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onWaiting={() => setReady(false)}
        onPlaying={() => setReady(true)}
        className="block h-full w-full object-cover"
      />
      {!playing ? (
        <span className="pointer-events-none absolute inset-0 grid place-items-center bg-black/15 transition-opacity group-hover:bg-black/25">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-white/90 text-[#1a1a1a] shadow-lg">
            {ready ? (
              <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" />
            ) : (
              <Loader2 className="h-6 w-6 animate-spin" />
            )}
          </span>
        </span>
      ) : null}
    </button>
  );
}
